<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import { CapexItemEditorFormSubmitEventPayload } from '@Quest/Capex/Components/CapexItemEditor'
    import type { CapexItemEditorViewModel } from '@Quest/Capex/Components/CapexItemEditor'
    import type { CapexAccountReadModel } from '@Quest/Capex/readmodels'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: CapexItemEditorViewModel
    }>()

    const emit = defineEmits<{
        (e: 'form-submit', payload: CapexItemEditorFormSubmitEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)
    const modalTitle = computed(() => vm.value.modalTitle)
    const confirmButtonLabel = computed(() => vm.value.confirmButtonLabel)

    const currencyLabel = computed(() => vm.value.currencyLabel)
    const accounts = computed<CapexAccountReadModel[]>(() => vm.value.accounts)

    // writable computeds (replace getter + setter pairs)

    const name = computed<string>({
        get: () => vm.value.name,
        set: v => { vm.value.name = v }
    })

    const accountId = computed<string | null>({
        get: () => vm.value.accountId,
        set: v => { vm.value.accountId = v }
    })

    const cost = computed<number | null>({
        get: () => vm.value.cost,
        set: v => { vm.value.cost = v }
    })

    const usefulLifeInYears = computed<number | null>({
        get: () => vm.value.usefulLifeInYears,
        set: v => { vm.value.usefulLifeInYears = v }
    })

    const acquisitionMonthIndex = computed<number | null>({
        get: () => vm.value.acquisitionMonthIndex,
        set: v => { vm.value.acquisitionMonthIndex = v }
    })

    const salvageValue = computed<number | null>({
        get: () => vm.value.salvageValue,
        set: v => { vm.value.salvageValue = v }
    })

    // ---------------- actions ----------------

    function handleFormSubmit()
    {
        emit(
            'form-submit',
            new CapexItemEditorFormSubmitEventPayload(
                vm.value.id,
                name.value,
                accountId.value,
                cost.value,
                usefulLifeInYears.value,
                acquisitionMonthIndex.value,
                salvageValue.value
            )
        )
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <form id="capex-item-editor-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>Name:</label>
                                <input type="text" v-model.trim="name" class="form-control" required minlength="2" maxlength="100" />
                            </div>
                            <div class="form-group">
                                <label for="account">Account:</label>
                                <select id="account" v-model="accountId" class="form-control" required>
                                    <option :value="null">Select capex account</option>
                                    <option v-for="a in accounts" :value="a.Id">{{a.Name}}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label>Cost ({{currencyLabel}}):</label>
                                <input type="number" v-model.number="cost" class="form-control" required min="0" step="any" />
                            </div>
                            <div class="form-group">
                                <label>Useful life (in years):</label>
                                <input type="number" v-model.number="usefulLifeInYears" class="form-control" required min="1" step="1" />
                            </div>
                            <div class="form-group">
                                <label>Capex Acquisition Month (0 = Project Start):</label>
                                <input type="number" v-model.number="acquisitionMonthIndex" class="form-control" required min="0" step="1" />
                            </div>
                            <div class="form-group">
                                <label>Salvage value ({{currencyLabel}}):</label>
                                <input type="number" v-model.number="salvageValue" class="form-control" />
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="capex-item-editor-form" class="btn btn-primary">{{ confirmButtonLabel }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>